import { useState, useCallback } from "react"
import { parseFilename, type ParsedFilename } from "../parseFilename"

export type UploadStatus = "pending" | "uploading" | "success" | "error"

export interface FileWithMetadata {
  id: string
  file: File
  metadata: ParsedFilename & {
    difficulty?: string
    duration?: number
    tags?: string[]
  }
  status: UploadStatus
  progress?: number
  error?: string
  file_key?: string
  file_size?: number
}

interface BatchUploadResult {
  filename: string
  success: boolean
  file_key?: string
  file_size?: number
  error?: string
}

interface BatchCreateResult {
  success: boolean
  id?: number
  error?: string
}

const CHUNK_SIZE = 15 // Upload 15 files at a time

export function useBatchUpload() {
  const [filesWithMetadata, setFilesWithMetadata] = useState<FileWithMetadata[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [overallProgress, setOverallProgress] = useState(0)

  // Add files and parse metadata
  const addFiles = useCallback((files: File[]) => {
    const newFiles: FileWithMetadata[] = files.map(file => {
      const parsed = parseFilename(file.name)
      return {
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        metadata: {
          ...parsed,
          duration: 120, // default
          tags: [],
        },
        status: "pending" as UploadStatus,
      }
    })
    setFilesWithMetadata(prev => [...prev, ...newFiles])
  }, [])

  // Update metadata for a specific file
  const updateFileMetadata = useCallback((id: string, updates: Partial<FileWithMetadata["metadata"]>) => {
    setFilesWithMetadata(prev =>
      prev.map(f => f.id === id ? { ...f, metadata: { ...f.metadata, ...updates } } : f)
    )
  }, [])

  // Remove a file from the queue
  const removeFile = useCallback((id: string) => {
    setFilesWithMetadata(prev => prev.filter(f => f.id !== id))
  }, [])

  // Clear all files
  const clearAll = useCallback(() => {
    setFilesWithMetadata([])
    setOverallProgress(0)
    setIsPaused(false)
  }, [])

  // Upload files in chunks
  const uploadFiles = useCallback(async () => {
    setIsProcessing(true)
    setIsPaused(false)

    const filesToUpload = filesWithMetadata.filter(f => f.status === "pending" || f.status === "error")
    let processedCount = 0
    const successfulUploads: Array<FileWithMetadata & { file_key: string; file_size: number }> = []

    // Process in chunks
    for (let i = 0; i < filesToUpload.length; i += CHUNK_SIZE) {
      if (isPaused) break

      const chunk = filesToUpload.slice(i, i + CHUNK_SIZE)

      // Mark files as uploading
      setFilesWithMetadata(prev =>
        prev.map(f => chunk.find(c => c.id === f.id) ? { ...f, status: "uploading" as UploadStatus } : f)
      )

      // Upload chunk to R2
      const formData = new FormData()
      chunk.forEach((fileData, index) => {
        formData.append(`file_${index}`, fileData.file)
      })

      try {
        const response = await fetch('/api/batch-upload', {
          method: 'POST',
          body: formData,
        })

        if (!response.ok) {
          throw new Error('Batch upload failed')
        }

        const result = await response.json() as { results: BatchUploadResult[] }

        // Update file statuses with upload results and track successful uploads
        setFilesWithMetadata(prev =>
          prev.map(f => {
            const uploadResult = result.results.find(r => r.filename === f.file.name)
            if (!uploadResult) return f

            if (uploadResult.success && uploadResult.file_key && uploadResult.file_size) {
              const updated = {
                ...f,
                status: "success" as UploadStatus,
                file_key: uploadResult.file_key,
                file_size: uploadResult.file_size,
              }
              // Track successful upload for paper creation
              successfulUploads.push(updated as FileWithMetadata & { file_key: string; file_size: number })
              return updated
            } else {
              return {
                ...f,
                status: "error" as UploadStatus,
                error: uploadResult.error || "Upload failed",
              }
            }
          })
        )

        processedCount += chunk.length
        setOverallProgress(Math.round((processedCount / filesToUpload.length) * 50)) // 50% for upload
      } catch (error) {
        // Mark chunk files as error
        setFilesWithMetadata(prev =>
          prev.map(f =>
            chunk.find(c => c.id === f.id)
              ? { ...f, status: "error" as UploadStatus, error: "Upload failed" }
              : f
          )
        )
        console.error("Chunk upload error:", error)
      }
    }

    // Now create paper records for successfully uploaded files
    if (successfulUploads.length > 0) {
      let paperProcessedCount = 0

      // Process paper creation in chunks
      for (let i = 0; i < successfulUploads.length; i += CHUNK_SIZE) {
        if (isPaused) break

        const chunk = successfulUploads.slice(i, i + CHUNK_SIZE)

        const papers = chunk.map(f => ({
          title: f.metadata.title || f.file.name,
          year: f.metadata.year || new Date().getFullYear(),
          region: f.metadata.region || "",
          subject: f.metadata.subject || "数学",
          difficulty: f.metadata.difficulty,
          tags: f.metadata.tags,
          duration: f.metadata.duration,
          file_key: f.file_key,
          file_size: f.file_size,
        }))

        try {
          const response = await fetch('/api/papers/batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ papers }),
          })

          if (!response.ok) {
            throw new Error('Batch paper creation failed')
          }

          const result = await response.json() as { results: BatchCreateResult[] }

          // Update statuses based on creation results
          setFilesWithMetadata(prev =>
            prev.map((f) => {
              const chunkIndex = chunk.findIndex(c => c.id === f.id)
              if (chunkIndex === -1) return f

              const createResult = result.results[chunkIndex]
              if (!createResult.success) {
                return { ...f, status: "error" as UploadStatus, error: createResult.error || "Failed to create paper record" }
              }
              return f
            })
          )

          paperProcessedCount += chunk.length
          setOverallProgress(50 + Math.round((paperProcessedCount / successfulUploads.length) * 50)) // 50-100%
        } catch (error) {
          console.error("Chunk paper creation error:", error)
          // Mark these files as error
          setFilesWithMetadata(prev =>
            prev.map(f =>
              chunk.find(c => c.id === f.id)
                ? { ...f, status: "error" as UploadStatus, error: "Failed to create paper record" }
                : f
            )
          )
        }
      }
    }

    setIsProcessing(false)
    setOverallProgress(100)
  }, [filesWithMetadata, isPaused])

  // Pause upload
  const pauseUpload = useCallback(() => {
    setIsPaused(true)
  }, [])

  // Resume upload
  const resumeUpload = useCallback(() => {
    setIsPaused(false)
    uploadFiles()
  }, [uploadFiles])

  // Retry failed uploads
  const retryFailed = useCallback(() => {
    setFilesWithMetadata(prev =>
      prev.map(f => f.status === "error" ? { ...f, status: "pending" as UploadStatus, error: undefined } : f)
    )
  }, [])

  // Get statistics
  const getStats = useCallback(() => {
    const total = filesWithMetadata.length
    const pending = filesWithMetadata.filter(f => f.status === "pending").length
    const uploading = filesWithMetadata.filter(f => f.status === "uploading").length
    const success = filesWithMetadata.filter(f => f.status === "success").length
    const error = filesWithMetadata.filter(f => f.status === "error").length

    return { total, pending, uploading, success, error }
  }, [filesWithMetadata])

  return {
    filesWithMetadata,
    isProcessing,
    isPaused,
    overallProgress,
    addFiles,
    updateFileMetadata,
    removeFile,
    clearAll,
    uploadFiles,
    pauseUpload,
    resumeUpload,
    retryFailed,
    getStats,
  }
}
